import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { PageSEO } from '../components/SEO';
import { 
  User, 
  Building, 
  Activity,
  FileText,
  Users,
  Euro,
  Folder,
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Success Screen Component - defined outside main component
const SellerSuccessScreen = ({ onNavigateMarketplace, onNavigateHome }) => (
  <>
    <PageSEO 
      title="Verkaufsanfrage erfolgreich | Firmbay"
      description="Ihre Verkaufsanfrage wurde erfolgreich übermittelt."
      path="/seller-onboarding"
      robots="noindex, nofollow"
    />
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-2xl mx-auto">
      <Card className="border-2 border-green-200 shadow-xl">
        <CardContent className="pt-12 pb-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Dein Angebot wurde eingereicht!
          </h1>
          
          <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
            Wir haben deine GmbH-Daten erfolgreich erhalten. Unser Team wird dein Angebot prüfen und sich in der Regel innerhalb von 48 Stunden bei dir melden.
          </p>

          <div className="bg-orange-50 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Wie geht es weiter?
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-600 mt-0.5">1.</span>
                <span>Wir prüfen die bereitgestellten Angaben auf Plausibilität und nehmen eine erste Einordnung für den Vermittlungsprozess vor.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-600 mt-0.5">2.</span>
                <span>Du erhältst eine E-Mail mit einer Einschätzung und möglichen nächsten Schritten.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-600 mt-0.5">3.</span>
                <span>Bei Interesse von Käufern begleiten wir dich durch den gesamten Verkaufsprozess.</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg"
              onClick={onNavigateMarketplace}
              className="bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700"
              data-testid="seller-success-marketplace-btn"
            >
              Zum Marktplatz
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={onNavigateHome}
              data-testid="seller-success-home-btn"
            >
              Zur Startseite
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
  </>
);

const SellerOnboardingForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // A. Angaben zum Anbieter
    seller_name: '',
    seller_email: '',
    seller_phone: '',
    seller_country: '',
    seller_role: [],
    
    // B. Angaben zur GmbH
    company_name: '',
    company_register_number: '',
    register_court: '',
    company_location: '',
    founding_date: '',
    share_capital: 25000,
    
    // C. Status der GmbH
    company_status: [],
    has_revenue: false,
    has_contracts: false,
    has_employees: false,
    
    // D. Finanzielle & rechtliche Situation
    has_liabilities: false,
    liabilities_description: '',
    has_tax_liabilities: 'no',
    financial_statement_available: 'yes',
    has_legal_disputes: false,
    legal_disputes_description: '',
    
    // E. Gesellschafter & Geschäftsführung
    number_of_shareholders: 1,
    ownership_distribution: '',
    has_managing_director: true,
    transfer_type: 'shares_only',
    
    // F. Preisvorstellung & Timing
    asking_price: null,
    negotiable: 'yes',
    transfer_timeline: 'one_month',
    
    // G. Unterlagen
    available_documents: [],
    
    // H. Erklärung & Zustimmung
    seller_confirmation: false,
  });

  const steps = [
    { number: 1, title: 'Anbieter', icon: User },
    { number: 2, title: 'GmbH-Daten', icon: Building },
    { number: 3, title: 'Status', icon: Activity },
    { number: 4, title: 'Finanzen', icon: FileText },
    { number: 5, title: 'Gesellschafter', icon: Users },
    { number: 6, title: 'Preis', icon: Euro },
    { number: 7, title: 'Unterlagen', icon: Folder },
    { number: 8, title: 'Bestätigung', icon: CheckCircle },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Normalisierung für optionale Number-Felder
    if (name === 'asking_price') {
      const normalized = value === '' ? null : Number(value);
      setFormData(prev => ({ ...prev, asking_price: normalized }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const handleNext = () => {
    // Minimal client-side Step-Validation (HTML required-Attribute greift nur beim finalen Submit)
    // -> Wir blocken "Weiter", wenn Pflichtfelder im aktuellen Schritt fehlen.
    if (currentStep === 1) {
      const ok =
        formData.seller_name.trim() &&
        formData.seller_email.trim() &&
        formData.seller_phone.trim() &&
        formData.seller_country.trim() &&
        formData.seller_role.length > 0;

      if (!ok) {
        toast({
          variant: "destructive",
          title: "Bitte vervollständigen",
          description: "Bitte fülle alle Pflichtfelder aus und wähle mindestens eine Rolle.",
        });
        return;
      }
    }

    if (currentStep === 2) {
      const ok =
        formData.company_name.trim() &&
        formData.company_register_number.trim() &&
        formData.register_court.trim() &&
        formData.company_location.trim() &&
        formData.founding_date &&
        Number(formData.share_capital) > 0;

      if (!ok) {
        toast({
          variant: "destructive",
          title: "Bitte vervollständigen",
          description: "Bitte fülle alle Pflichtfelder aus.",
        });
        return;
      }
    }

    if (currentStep === 3) {
      if (formData.company_status.length === 0) {
        toast({
          variant: "destructive",
          title: "Bitte Auswahl treffen",
          description: "Bitte wähle mindestens einen Status aus.",
        });
        return;
      }
    }

    if (currentStep === 5) {
      const ok = Number(formData.number_of_shareholders) >= 1 && formData.ownership_distribution.trim();
      if (!ok) {
        toast({
          variant: "destructive",
          title: "Bitte vervollständigen",
          description: "Bitte gib die Anzahl der Gesellschafter und die Beteiligungsverhältnisse an.",
        });
        return;
      }
    }

    setCurrentStep(prev => Math.min(prev + 1, 8));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.seller_confirmation) {
      toast({
        variant: "destructive",
        title: "Bestätigung erforderlich",
        description: "Bitte bestätige die rechtlichen Hinweise.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/seller-onboarding`, formData);
      
      if (response.status === 201) {
        setIsSuccess(true);
      }
    } catch (error) {
      console.error('Error submitting seller form:', error);
      toast({
        variant: "destructive",
        title: "Fehler beim Senden",
        description: error.response?.data?.detail || "Bitte versuche es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success screen if form was submitted successfully
  if (isSuccess) {
    return (
      <SellerSuccessScreen 
        onNavigateMarketplace={() => navigate('/marktplatz')} 
        onNavigateHome={() => navigate('/')} 
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">A. Angaben zum Anbieter (Verkäufer)</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vollständiger Name *
              </label>
              <input
                type="text"
                name="seller_name"
                required
                value={formData.seller_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Max Mustermann"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  name="seller_email"
                  required
                  value={formData.seller_email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="max@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefonnummer *
                </label>
                <input
                  type="tel"
                  name="seller_phone"
                  required
                  value={formData.seller_phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="+49 123 456789"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wohnsitzland *
              </label>
              <input
                type="text"
                name="seller_country"
                required
                value={formData.seller_country}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Deutschland"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Rolle (Mehrfachauswahl möglich) *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'shareholder', label: 'Gesellschafter' },
                  { value: 'managing_director', label: 'Geschäftsführer' },
                  { value: 'authorized', label: 'Bevollmächtigter' }
                ].map(role => (
                  <div key={role.value} className="flex items-center space-x-3">
                    <Checkbox
                      id={`role_${role.value}`}
                      checked={formData.seller_role.includes(role.value)}
                      onCheckedChange={() => handleArrayChange('seller_role', role.value)}
                    />
                    <label htmlFor={`role_${role.value}`} className="text-sm text-gray-700">
                      {role.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">B. Angaben zur GmbH</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Firmenname laut Handelsregister *
              </label>
              <input
                type="text"
                name="company_name"
                required
                value={formData.company_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Meine Firma GmbH"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Handelsregisternummer *
                </label>
                <input
                  type="text"
                  name="company_register_number"
                  required
                  value={formData.company_register_number}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="HRB 12345"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registergericht *
                </label>
                <input
                  type="text"
                  name="register_court"
                  required
                  value={formData.register_court}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Amtsgericht Berlin"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sitz der GmbH *
                </label>
                <input
                  type="text"
                  name="company_location"
                  required
                  value={formData.company_location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Berlin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gründungsdatum *
                </label>
                <input
                  type="date"
                  name="founding_date"
                  required
                  value={formData.founding_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stammkapital (€) *
              </label>
              <input
                type="number"
                name="share_capital"
                required
                min="1"
                value={formData.share_capital}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="25000"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">C. Status der GmbH</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Bitte auswählen (Mehrfachauswahl möglich) *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'shelf', label: 'Vorratsgesellschaft (keine operative Tätigkeit)' },
                  { value: 'dormant', label: 'Ruhende GmbH (früher tätig, aktuell inaktiv)' },
                  { value: 'active', label: 'Operativ tätige GmbH' }
                ].map(status => (
                  <div key={status.value} className="flex items-start space-x-3">
                    <Checkbox
                      id={`status_${status.value}`}
                      checked={formData.company_status.includes(status.value)}
                      onCheckedChange={() => handleArrayChange('company_status', status.value)}
                    />
                    <label htmlFor={`status_${status.value}`} className="text-sm text-gray-700">
                      {status.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <h4 className="font-semibold text-gray-900">Zusatzfragen:</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wurde jemals Umsatz erzielt? *
                </label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="revenue_yes"
                      name="has_revenue"
                      checked={formData.has_revenue === true}
                      onChange={() => setFormData(prev => ({ ...prev, has_revenue: true }))}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor="revenue_yes" className="text-sm text-gray-700">Ja</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="revenue_no"
                      name="has_revenue"
                      checked={formData.has_revenue === false}
                      onChange={() => setFormData(prev => ({ ...prev, has_revenue: false }))}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor="revenue_no" className="text-sm text-gray-700">Nein</label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bestehen laufende Verträge? *
                </label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="contracts_yes"
                      name="has_contracts"
                      checked={formData.has_contracts === true}
                      onChange={() => setFormData(prev => ({ ...prev, has_contracts: true }))}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor="contracts_yes" className="text-sm text-gray-700">Ja</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="contracts_no"
                      name="has_contracts"
                      checked={formData.has_contracts === false}
                      onChange={() => setFormData(prev => ({ ...prev, has_contracts: false }))}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor="contracts_no" className="text-sm text-gray-700">Nein</label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gibt es Mitarbeiter? *
                </label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="employees_yes"
                      name="has_employees"
                      checked={formData.has_employees === true}
                      onChange={() => setFormData(prev => ({ ...prev, has_employees: true }))}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor="employees_yes" className="text-sm text-gray-700">Ja</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="employees_no"
                      name="has_employees"
                      checked={formData.has_employees === false}
                      onChange={() => setFormData(prev => ({ ...prev, has_employees: false }))}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor="employees_no" className="text-sm text-gray-700">Nein</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">D. Finanzielle & rechtliche Situation</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bestehen Verbindlichkeiten? *
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="liabilities_no"
                    name="has_liabilities"
                    checked={formData.has_liabilities === false}
                    onChange={() => setFormData(prev => ({ ...prev, has_liabilities: false, liabilities_description: '' }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="liabilities_no" className="text-sm text-gray-700">Nein</label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="liabilities_yes"
                    name="has_liabilities"
                    checked={formData.has_liabilities === true}
                    onChange={() => setFormData(prev => ({ ...prev, has_liabilities: true }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="liabilities_yes" className="text-sm text-gray-700">Ja (bitte erläutern)</label>
                </div>
              </div>
              
              {formData.has_liabilities && (
                <textarea
                  name="liabilities_description"
                  value={formData.liabilities_description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full mt-3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Bitte beschreibe die Verbindlichkeiten..."
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Liegen offene Steuerverbindlichkeiten vor? *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'no', label: 'Nein' },
                  { value: 'yes', label: 'Ja' },
                  { value: 'unknown', label: 'Unbekannt' }
                ].map(option => (
                  <div key={option.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id={`tax_${option.value}`}
                      name="has_tax_liabilities"
                      value={option.value}
                      checked={formData.has_tax_liabilities === option.value}
                      onChange={handleChange}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor={`tax_${option.value}`} className="text-sm text-gray-700">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aktueller Jahresabschluss vorhanden? *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'yes', label: 'Ja' },
                  { value: 'in_preparation', label: 'In Vorbereitung' },
                  { value: 'no', label: 'Nein' }
                ].map(option => (
                  <div key={option.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id={`statement_${option.value}`}
                      name="financial_statement_available"
                      value={option.value}
                      checked={formData.financial_statement_available === option.value}
                      onChange={handleChange}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor={`statement_${option.value}`} className="text-sm text-gray-700">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bestehen laufende Rechtsstreitigkeiten? *
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="disputes_no"
                    name="has_legal_disputes"
                    checked={formData.has_legal_disputes === false}
                    onChange={() => setFormData(prev => ({ ...prev, has_legal_disputes: false, legal_disputes_description: '' }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="disputes_no" className="text-sm text-gray-700">Nein</label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="disputes_yes"
                    name="has_legal_disputes"
                    checked={formData.has_legal_disputes === true}
                    onChange={() => setFormData(prev => ({ ...prev, has_legal_disputes: true }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="disputes_yes" className="text-sm text-gray-700">Ja (bitte beschreiben)</label>
                </div>
              </div>
              
              {formData.has_legal_disputes && (
                <textarea
                  name="legal_disputes_description"
                  value={formData.legal_disputes_description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full mt-3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Bitte beschreibe die Rechtsstreitigkeiten..."
                />
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">E. Gesellschafter & Geschäftsführung</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anzahl der Gesellschafter *
                </label>
                <input
                  type="number"
                  name="number_of_shareholders"
                  required
                  min="1"
                  value={formData.number_of_shareholders}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aktuelle Beteiligungsverhältnisse *
                </label>
                <input
                  type="text"
                  name="ownership_distribution"
                  required
                  value={formData.ownership_distribution}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="z.B. 100%, 50/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geschäftsführer aktuell vorhanden? *
              </label>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="director_yes"
                    name="has_managing_director"
                    checked={formData.has_managing_director === true}
                    onChange={() => setFormData(prev => ({ ...prev, has_managing_director: true }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="director_yes" className="text-sm text-gray-700">Ja</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="director_no"
                    name="has_managing_director"
                    checked={formData.has_managing_director === false}
                    onChange={() => setFormData(prev => ({ ...prev, has_managing_director: false }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="director_no" className="text-sm text-gray-700">Nein</label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gewünschter Ablauf *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'shares_only', label: 'Verkauf der Geschäftsanteile' },
                  { value: 'shares_and_director', label: 'Verkauf + Geschäftsführerwechsel' },
                  { value: 'consultation_needed', label: 'Unklar / Beratung gewünscht' }
                ].map(option => (
                  <div key={option.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id={`transfer_${option.value}`}
                      name="transfer_type"
                      value={option.value}
                      checked={formData.transfer_type === option.value}
                      onChange={handleChange}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor={`transfer_${option.value}`} className="text-sm text-gray-700">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">F. Preisvorstellung & Timing</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preisvorstellung (freiwillig, €)
              </label>
              <input
                type="number"
                name="asking_price"
                min="0"
                value={formData.asking_price || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="z.B. 10000"
              />
              <p className="text-xs text-gray-500 mt-1">Optional – wir können dich auch bei der Bewertung unterstützen</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verhandlungsbereit? *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'yes', label: 'Ja' },
                  { value: 'no', label: 'Nein' },
                  { value: 'depends', label: 'Abhängig vom Käufer' }
                ].map(option => (
                  <div key={option.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id={`negotiable_${option.value}`}
                      name="negotiable"
                      value={option.value}
                      checked={formData.negotiable === option.value}
                      onChange={handleChange}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor={`negotiable_${option.value}`} className="text-sm text-gray-700">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wunschzeitpunkt der Übertragung *
              </label>
              <div className="space-y-2">
                {[
                  { value: 'short_term', label: 'kurzfristig (1–2 Wochen)' },
                  { value: 'one_month', label: 'innerhalb 1 Monats' },
                  { value: 'later', label: 'später' }
                ].map(option => (
                  <div key={option.value} className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id={`timeline_${option.value}`}
                      name="transfer_timeline"
                      value={option.value}
                      checked={formData.transfer_timeline === option.value}
                      onChange={handleChange}
                      className="w-4 h-4 text-orange-600"
                    />
                    <label htmlFor={`timeline_${option.value}`} className="text-sm text-gray-700">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">G. Unterlagen</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Welche Unterlagen können bereitgestellt werden? (optional)
              </label>
              <div className="space-y-2">
                {[
                  { value: 'register_extract', label: 'Handelsregisterauszug' },
                  { value: 'articles', label: 'Gesellschaftsvertrag' },
                  { value: 'financial_statement', label: 'Letzter Jahresabschluss' },
                  { value: 'shareholder_list', label: 'Gesellschafterliste' },
                  { value: 'other', label: 'Sonstige Unterlagen' }
                ].map(doc => (
                  <div key={doc.value} className="flex items-center space-x-3">
                    <Checkbox
                      id={`doc_${doc.value}`}
                      checked={formData.available_documents.includes(doc.value)}
                      onCheckedChange={() => handleArrayChange('available_documents', doc.value)}
                    />
                    <label htmlFor={`doc_${doc.value}`} className="text-sm text-gray-700">
                      {doc.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-sm text-gray-700">
                <strong>Hinweis:</strong> Die tatsächlichen Unterlagen können später hochgeladen oder per E-Mail nachgereicht werden. 
                Diese Angabe dient nur der Vorbereitung.
              </p>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">H. Erklärung & Zustimmung</h3>
            
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Wichtige Erklärung</h4>
                  <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                    <p>Ich bestätige, dass die gemachten Angaben vollständig und nach bestem Wissen korrekt sind.</p>
                    <p>Mir ist bekannt, dass Firmbay keine Gesellschaften verkauft, sondern lediglich die Vermittlung und organisatorische Begleitung einer möglichen GmbH-Übertragung übernimmt.</p>
                    <p>Die Übertragung erfolgt ausschließlich notariell.</p>
                  </div>
                  
                  <div className="mt-6 flex items-start space-x-3">
                    <Checkbox
                      id="seller_confirmation"
                      checked={formData.seller_confirmation}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, seller_confirmation: checked }))
                      }
                    />
                    <label htmlFor="seller_confirmation" className="text-sm font-medium text-gray-900">
                      Ich bestätige die oben genannten Hinweise *
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Zusammenfassung deines Angebots</h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Name:</span> {formData.seller_name}</p>
                <p><span className="font-medium">E-Mail:</span> {formData.seller_email}</p>
                <p><span className="font-medium">Firmenname:</span> {formData.company_name}</p>
                <p><span className="font-medium">Handelsregisternummer:</span> {formData.company_register_number}</p>
                <p><span className="font-medium">Sitz:</span> {formData.company_location}</p>
                <p><span className="font-medium">Stammkapital:</span> {formData.share_capital?.toLocaleString('de-DE')} €</p>
                <p><span className="font-medium">Preisvorstellung:</span> {formData.asking_price ? `${formData.asking_price.toLocaleString('de-DE')} €` : 'Keine Angabe'}</p>
                <p><span className="font-medium">Zeitpunkt:</span> {
                  formData.transfer_timeline === 'short_term' ? 'Kurzfristig (1-2 Wochen)' :
                  formData.transfer_timeline === 'one_month' ? 'Innerhalb 1 Monats' : 'Später'
                }</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <PageSEO 
        title="GmbH verkaufen – Anfrageformular | Firmbay"
        description="Verkaufen Sie Ihre Bestands-GmbH über Firmbay. Erfassungsformular für Verkäufer."
        path="/seller-onboarding"
        robots="noindex, nofollow"
      />
      <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="pt-40 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            GmbH verkaufen
            <span className="block mt-2 pb-1 leading-tight bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
              Anfrageformular
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Biete deine Bestands-GmbH zum Verkauf an
          </p>
          
          {/* Disclaimer */}
          <div className="max-w-3xl mx-auto bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-left">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-800 leading-relaxed">
                <p className="font-semibold mb-2">Wichtiger Hinweis:</p>
                <p>Dieses Formular dient ausschließlich der Vermittlung und organisatorischen Vorbereitung einer möglichen GmbH-Übertragung. Es erfolgt keine Rechts- oder Steuerberatung. Die Übertragung von Geschäftsanteilen erfolgt ausschließlich notariell.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between overflow-x-auto pb-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.number === currentStep;
                const isCompleted = step.number < currentStep;
                
                return (
                  <React.Fragment key={step.number}>
                    <div className="flex flex-col items-center min-w-max">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        isActive 
                          ? 'bg-gradient-to-r from-orange-600 to-yellow-600 text-white shadow-lg' 
                          : isCompleted
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${
                        isActive ? 'text-orange-600' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 rounded transition-all min-w-[20px] ${
                        isCompleted ? 'bg-teal-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Form Card */}
          <Card className="border-2 border-gray-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">
                Schritt {currentStep} von 8
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={currentStep === 8 ? handleSubmit : (e) => e.preventDefault()}>
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="border-2 border-gray-300"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>

                  {currentStep < 8 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white"
                    >
                      Weiter
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting || !formData.seller_confirmation}
                      className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white"
                    >
                      {isSubmitting ? 'Wird gesendet...' : 'Angebot absenden'}
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
    </>
  );
};

export default SellerOnboardingForm;
